import { forwardRef, memo } from 'react';
import type { ChangeEvent, InputHTMLAttributes } from 'react';
import { Button, Typography } from '@mui/material';
import { PickerInput, PickerWrapper, Wrapper } from './ColorInput.styles';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';

export interface ColorInputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  fullWidth?: boolean;
  dataTestId?: string;
}

export const ColorInput = memo(
  forwardRef<HTMLInputElement, ColorInputProps>(({ label, error, fullWidth, dataTestId, ...props }, ref) => {
    const { t } = useTranslation();
    const handleClear = () => {
      if (props.onChange) {
        const event = {
          target: {
            name: props.name,
            value: '',
          },
        };
        props.onChange(event as ChangeEvent<HTMLInputElement>);
      }
    };

    return (
      <Wrapper>
        {label && <label htmlFor={props.id}>{label}</label>}
        <PickerWrapper>
          <PickerInput
            type="color"
            disabled={props?.disabled}
            name={props?.name}
            defaultValue={props?.defaultValue}
            value={props?.value}
            ref={ref}
            data-testid={dataTestId}
            {...props}
          />
          <Typography variant="body2" color="textPrimary">
            {props?.value}
          </Typography>
          {props?.value ? (
            <Button onClick={handleClear} size="small" variant="text" data-testid={dataTestId ? `${dataTestId}-clear-button` : undefined}>
              <DeleteIcon />
            </Button>
          ) : (
            <span>{t('common.notSelected')}</span>
          )}
        </PickerWrapper>
        {error && (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        )}
      </Wrapper>
    );
  })
);
